import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CustomSelect } from 'src/components/custom-select'
import { useToast } from 'src/components/toast'
import { SelectOption } from 'src/services/base-model'
import { userApi } from 'src/services/user/api'
import { useUser } from 'src/services/user/hooks'
import { userQueries } from 'src/services/user/queries'

/**
 * Lets the user pick which of their organizations is charged for work done
 * before an org is known. Hidden when there is nothing to choose between.
 */
export const DefaultOrganizationSection = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data: user } = useUser()
  const memberships = user?.memberships

  const options: SelectOption[] = useMemo(
    () => (memberships ?? []).map((m) => ({ label: m.organizationName, value: m.organizationId })),
    [memberships],
  )
  const selected = options.filter((o) => o.value === user?.defaultOrganization)

  const updateDefault = useMutation({
    mutationFn: (organizationId: string) =>
      userApi.update({ id: user!.id, defaultOrganization: organizationId }),
    onSuccess: (updated) => {
      queryClient.setQueryData(userQueries.retrieve(updated.id).queryKey, updated)
      toast({ description: t('defaultOrganization.saved'), type: 'success' })
    },
    onError: () => {
      toast({ description: t('defaultOrganization.saveError'), type: 'error' })
    },
  })

  if (!user || options.length <= 1) return null

  return (
    <div
      className="rounded-lg border border-border bg-surface p-6 shadow-sm"
      data-testid="default-organization-section"
    >
      <div className="mb-4">
        <h3 className="text-lg font-medium text-text-primary">{t('defaultOrganization.title')}</h3>
        <p className="mt-1 text-sm text-text-placeholder">{t('defaultOrganization.help')}</p>
      </div>
      <div className="max-w-sm">
        <CustomSelect
          options={options}
          values={selected}
          onChange={(values) => {
            const value = values[0]?.value
            if (value && value !== user.defaultOrganization) updateDefault.mutate(value)
          }}
          placeholder={t('defaultOrganization.placeholder')}
          multi={false}
          clearable={false}
          disabled={updateDefault.isPending}
        />
      </div>
    </div>
  )
}
